import React from 'react';
import { useState, useEffect, useRef } from 'react';
import './classes/JobsInSportsBanner.css';
import { configData } from "../config";
import PhoneInput, { isValidPhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import ReCAPTCHA from "react-google-recaptcha";
import { useQuickApp, submitQuickAppStep1, isBlockedEmail, QUICK_APP_MESSAGES } from "./services/QuickAppService";
import { loadCountryStateLists, CANADIAN_PROVINCES } from "./services/CountryStateLists";
import { openQuickAppPopup } from "./QuickAppPopup";

// The "jobs in sports" landing pages: a full-width banner under the header with the
// "Apply Today" form on it, as on the Drupal site. The pages and what each one shows are in
// constants/jobsInSportsPages.js.
import { JOBS_IN_SPORTS_PAGES } from '../constants/jobsInSportsPages';
export { JOBS_IN_SPORTS_PAGES };

const JobsInSportsBanner = ({ page }) => {
    const images_url = configData.DRUPAL_LIVE_DOMAIN_URL + '/sites/jobs_in_sports_images/';
    const banner = JOBS_IN_SPORTS_PAGES[page?.nid];
    const recaptchaRef = useRef(null);

    const [formData, setFormData] = useState({
        fname: '',
        lname: '',
        email: '',
        phone: `+${getCountryCallingCode('US')}`,
        fteam: '',
        fcity: '',
        fcountry: 'United States',
        fstate: '',
        fprovince: '',
        field_text_me_with_news_offers: 0,
        sku: banner?.sku || '',
        page_type: 'jobs_in_sports',
        pageURL: window.location.href,
    });
    const [countriesList, setCountriesList] = useState([]);
    const [statelist, setStateList] = useState([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    // The form is step 1 of the application: once it is saved, steps 2 and 3 follow in a popup
    const application = useQuickApp();

    // The states come listed by name, as the live form lists them
    useEffect(() => {
        loadCountryStateLists().then(({ countries, states }) => {
            setCountriesList(countries);
            setStateList(states);
        });
    }, []);

    if (!banner) return null;

    // States are asked for the US and Canada, a province for everywhere else
    const showState = formData.fcountry === 'United States' || formData.fcountry === 'Canada';

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
        } else if (name === 'fcountry') {
            setFormData(prev => ({ ...prev, fcountry: value, fstate: '', fprovince: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // The first problem found, in the order the Drupal form checked them
    const validate = (data) => {
        if (!data.fname.trim()) return 'Please enter your first name.';
        if (!data.lname.trim()) return 'Please enter your last name.';
        if (!/^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(data.email.trim())) return 'Please enter a valid email address.';
        if (isBlockedEmail(data.email)) return QUICK_APP_MESSAGES.email_blocked;
        if (!data.phone || !isValidPhoneNumber(data.phone)) return 'Please fill in valid primary phone number.';
        if (!data.fcountry) return 'Please Select Country';
        // "-" is "Outside the US or Canada", which is not a state of the country chosen
        if (showState && (!data.fstate || data.fstate === '-')) return 'Please Select State';
        if (!data.fcity.trim()) return 'Please enter your city.';
        if (!data.fteam.trim()) return 'Please enter your favorite team.';
        if (!recaptchaRef.current?.getValue()) return "Please verify you're not a robot.";
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const formError = validate(formData);
        setError(formError);
        if (formError) return;

        setSaving(true);
        // Step 1 of the application (application/step1), as the Drupal form posts it
        const saved = await submitQuickAppStep1(formData, countriesList);
        setSaving(false);
        if (saved) {
            openQuickAppPopup();
        } else {
            setError('Something went wrong. Please try again.');
        }
    };

    return (
        // jobs_in_sports_banner is the Drupal class: some page bodies carry their own banner heights for it
        <div className="jobs-in-sports-banner jobs_in_sports_banner" style={{ '--jobs-banner-image': `url("${images_url + banner.image}")` }}>
            {/* Shown instead of the background on tablets and phones */}
            <img className="jobs-in-sports-banner__mobile-image" src={images_url + banner.mobileImage} alt="" />

            <div className="jobs-in-sports-banner__form">
                <div className="jobs-in-sports-banner__title">APPLY TODAY</div>

                {application ? (
                    application.stage === 4 ? (
                    <div className="jobs-in-sports-banner__thanks">
                        Thank you for your interest!<br /> Someone from SMWW will be contacting you soon.
                    </div>
                    ) : (
                    <div className="sqa-resume">
                        <p>Thank you, {application.state.first_name}! You’re one step in.</p>
                        <button type="button" className="sqa-btn" onClick={openQuickAppPopup}>Finish your application</button>
                    </div>
                    )
                ) : (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-fname">First Name</label>
                            <input id="jobs-fname" name="fname" type="text" value={formData.fname} onChange={handleChange} />
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-lname">Last Name</label>
                            <input id="jobs-lname" name="lname" type="text" value={formData.lname} onChange={handleChange} />
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-email">Email</label>
                            <input id="jobs-email" name="email" type="text" value={formData.email} onChange={handleChange} />
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-phone">Phone</label>
                            <PhoneInput
                                id="jobs-phone"
                                international
                                countryCallingCodeEditable={false}
                                defaultCountry="US"
                                value={formData.phone}
                                onChange={(phone) => setFormData(prev => ({ ...prev, phone: phone || '' }))}
                            />
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-fcity">City</label>
                            <input id="jobs-fcity" name="fcity" type="text" value={formData.fcity} onChange={handleChange} />
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-fcountry">Country</label>
                            <select id="jobs-fcountry" name="fcountry" value={formData.fcountry} onChange={handleChange}>
                                <option value="">Select Country</option>
                                {Object.keys(countriesList).map(item => (
                                    <option key={countriesList[item].label} value={countriesList[item].label}>{countriesList[item].label}</option>
                                ))}
                            </select>
                        </div>
                        {showState ? (
                            <div className="jobs-in-sports-banner__field">
                                <label htmlFor="jobs-fstate">State</label>
                                <select id="jobs-fstate" name="fstate" value={formData.fstate} onChange={handleChange}>
                                    <option value="">Select a State or Province</option>
                                    <option value="-">Outside the US or Canada</option>
                                    {/* The chosen country's states alone, as live's smww-quickapp.js narrows
                                        the list (its own lists, with Wyoming in the US) */}
                                    {statelist.filter(state => CANADIAN_PROVINCES.includes(state.abbreviation) === (formData.fcountry === 'Canada')).map(state => (
                                        <option key={state.name} value={state.name}>{state.name}</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="jobs-in-sports-banner__field">
                                <label htmlFor="jobs-fprovince">Province</label>
                                <input id="jobs-fprovince" name="fprovince" type="text" value={formData.fprovince} onChange={handleChange} />
                            </div>
                        )}
                        <div className="jobs-in-sports-banner__field">
                            <label htmlFor="jobs-fteam">Favorite Team or Club?</label>
                            <input id="jobs-fteam" name="fteam" type="text" value={formData.fteam} onChange={handleChange} />
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <label className="jobs-in-sports-banner__consent">
                                <input name="field_text_me_with_news_offers" type="checkbox" checked={formData.field_text_me_with_news_offers === 1} onChange={handleChange} />
                                {' '}Text me with news and offers
                            </label>
                        </div>
                        <div className="jobs-in-sports-banner__field">
                            <div className="jobs-in-sports-banner__recaptcha">
                                <ReCAPTCHA ref={recaptchaRef} sitekey={configData.GOOGLE_RECAPTCHA_SITE_KEY} />
                            </div>
                        </div>
                        <div className="jobs-in-sports-banner__actions">
                            {error && <span className="jobs-in-sports-banner__error">{error}</span>}
                            <button type="submit" aria-disabled={saving ? "true" : undefined} style={saving ? { opacity: 0.7 } : undefined}>Submit</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default JobsInSportsBanner;
